import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { findStudentByName } from '../utils/studentMatcher';
import { addInternshipEvaluations } from '../api/crud';
import { updateSnackbar } from '../redux/action/dataAction';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  FormHelperText,
  Alert,
  Chip
} from '@mui/material';

const BulkInputModal = ({ 
  open, 
  onClose, 
  inputType, 
  tableData = [],
  onRefresh = null // เพิ่ม callback สำหรับ refresh ข้อมูล
}) => {
  const [bulkInputData, setBulkInputData] = useState('');
  const [previewData, setPreviewData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false); // เพิ่ม state สำหรับแสดงสถานะการประมวลผล
  const dispatch = useDispatch();

  // Update preview data when input changes
  useEffect(() => {
    // Clean up - removed debug logging
  }, [previewData]);

  // Normalize Google Sheets data to handle line breaks within cells
  const normalizeGoogleSheetsData = (text) => {
    if (!text || text.trim() === '') return '';
    const lines = text.split('\n');
    const processedLines = [];
    let currentLine = '';
    let insideQuotes = false;
    let quoteBalance = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      // นับจำนวน quotes ในบรรทัดนี้
      let quoteCount = 0;
      for (let char of line) {
        if (char === '"') quoteCount++;
      }
      quoteBalance += quoteCount;
      insideQuotes = (quoteBalance % 2 === 1);

      // เงื่อนไขการเริ่มแถวใหม่:
      // 1. บรรทัดแรก
      // 2. ไม่อยู่ใน quotes และ (มี timestamp + tab >= 10) หรือ tab >= 35 หรือ tab >= 30 และยาว > 50
      const currentTabCount = (line.match(/\t/g) || []).length;
      const hasTimestampPattern = line.match(/^\d{1,2}\/\d{1,2}\/\d{4}/);
      const isNewRow = i === 0 ||
        (!insideQuotes && (
          (hasTimestampPattern && currentTabCount >= 10) ||
          currentTabCount >= 35 ||
          (currentTabCount >= 30 && line.trim().length > 50)
        ));

      if (isNewRow) {
        if (currentLine.trim()) {
          processedLines.push(currentLine.trim());
        }
        currentLine = line;
        // รีเซ็ต quote balance สำหรับแถวใหม่
        quoteBalance = quoteCount;
        insideQuotes = (quoteBalance % 2 === 1);
      } else {
        // ถ้าอยู่ใน quotes หรือ quote ยังไม่ครบคู่ ให้รวมบรรทัดนี้กับ currentLine
        if (currentLine) {
          currentLine += '\n' + line; // ใช้ \n เพื่อเก็บ line break จริงใน cell
        } else {
          currentLine = line;
        }
      }
    }
    if (currentLine.trim()) {
      processedLines.push(currentLine.trim());
    }
    return processedLines.join('\n');
  };

  // Parse bulk input data from Google Sheets format
  const parseBulkData = (inputText) => {
    // จัดการข้อมูลที่มี line breaks ภายในเซลล์
    const normalizedText = normalizeGoogleSheetsData(inputText);
    const lines = normalizedText.trim().split('\n');
    const parsedData = [];
    
    // Debug: แสดงจำนวนบรรทัดที่ประมวลผล
    console.log(`Processing ${lines.length} lines`);
    
    lines.forEach((line, index) => {
      const columns = line.trim().split('\t');
      
      // ลดเงื่อนไขให้หลวมขึ้น - ตรวจสอบว่ามีข้อมูลพื้นฐานเพียงพอ
      // ควรมีอย่างน้อย 10 คอลัมน์ และมีข้อมูลนิสิต หรือ timestamp
      if (columns.length >= 10 && (columns[5]?.trim() || columns[0]?.trim())) { 
        // ข้อมูลจาก Google Sheets ไม่มี req_id ในคอลัมน์แรก ให้สร้าง temp id
        const temp_req_id = `temp-${index + 1}`;
        
        const student_name = columns[5]?.trim() || '';
        
        // ค้นหา req_id จากชื่อนิสิต
        let found_req_id = null;
        let found_student = null;
        
        // ค้นหา req_id จากชื่อนิสิตโดยใช้ utility function
        if (student_name && tableData && tableData.length > 0) {
          found_student = findStudentByName(student_name, tableData);
          if (found_student) {
            found_req_id = found_student.req_id;
          }
        }

        // ตรวจสอบว่ามีข้อมูลที่มี line breaks หรือ quotes
        const hasSpecialChars = line.includes('\n') || line.includes('"') || 
                               columns.some(col => col && (col.includes('\n') || col.includes('"')));

        // Helper function to clean cell data
        const cleanCellData = (cellData) => {
          if (!cellData) return '';
          let cleaned = cellData.toString().trim();
          
          // ลบ double quotes ที่ Google Sheets ใส่เมื่อมี line breaks หรือ special characters
          // ตรวจสอบว่าเริ่มและจบด้วย quote และไม่มี quote ตรงกลาง
          if (cleaned.startsWith('"') && cleaned.endsWith('"') && cleaned.length >= 2) {
            const middle = cleaned.slice(1, -1);
            // ถ้าไม่มี quote ตรงกลางหรือมีแต่ escaped quotes ("")
            if (!middle.includes('"') || middle.replace(/""/g, '').indexOf('"') === -1) {
              cleaned = middle;
            }
          }
          
          return cleaned
            .replace(/""/g, '"')      // แทนที่ escaped quotes ("") ด้วย single quote
            .replace(/\s+/g, ' ')     // แทนที่ช่องว่างหลายตัวด้วยช่องว่างเดียว
            .replace(/\n/g, ' ')      // แทนที่ line break ด้วยช่องว่าง
            .replace(/\r/g, '')       // ลบ carriage return
            .replace(/\t/g, ' ')      // แทนที่ tab ด้วยช่องว่าง
            .trim();                  // ลบช่องว่างหัวท้าย
        };

        parsedData.push({
          temp_id: temp_req_id, // ใช้ temp id สำหรับการแสดงผล
          found_req_id: found_req_id, // req_id ที่ค้นหาได้
          found_student: found_student, // ข้อมูลนิสิตที่ค้นหาได้
          timestamp_text: cleanCellData(columns[0]), // ประทับเวลา
          email: cleanCellData(columns[1]), // ที่อยู่อีเมล
          evaluator_name: cleanCellData(columns[2]), // ชื่อ-สกุล ผู้ประเมิน
          evaluator_position: cleanCellData(columns[3]), // ตำแหน่ง
          evaluator_phone: cleanCellData(columns[4]), // เบอร์โทรติดต่อ
          student_name: cleanCellData(columns[5]), // ชื่อ-นามสกุล นิสิตฝึกงาน
          program: cleanCellData(columns[6]), // สาขาวิชา
          company: cleanCellData(columns[7]), // สถานประกอบการ/แผนก
          period: cleanCellData(columns[8]), // ช่วงเวลาในการประเมิน
          // คะแนนการประเมิน (columns 9-23) - 15 หัวข้อการประเมิน
          eval_work_effectiveness: cleanCellData(columns[9]), // ประสิทธิผลของงาน
          eval_initiative: cleanCellData(columns[10]), // การริเริ่ม การประยุกต์
          eval_communication: cleanCellData(columns[11]), // ความสามารถในการสื่อสาร
          eval_learning: cleanCellData(columns[12]), // ความสนใจในการเรียนรู้
          eval_compliance: cleanCellData(columns[13]), // การปฏิบัติตามกฎระเบียบ
          eval_responsibility: cleanCellData(columns[14]), // ความรับผิดชอบและตรงเวลา
          eval_leadership: cleanCellData(columns[15]), // ความกล้าแสดงความคิดเห็น
          eval_relationship: cleanCellData(columns[16]), // มนุษยสัมพันธ์ในการทำงาน
          eval_teamwork: cleanCellData(columns[17]), // ความสามารถทำงานเป็นทีม
          eval_confidence: cleanCellData(columns[18]), // เชื่อมั่นในความสามารถตนเอง
          eval_consideration: cleanCellData(columns[19]), // การเห็นแก่ประโยชน์ผู้อื่น
          eval_commitment: cleanCellData(columns[20]), // มุ่งมั่นและพยายาม
          eval_enthusiasm: cleanCellData(columns[21]), // กระตือรือร้นในการเรียนรู้
          eval_improvement: cleanCellData(columns[22]), // ปรับปรุงพัฒนาตนเอง
          eval_respect: cleanCellData(columns[23]), // มีความเคารพ อ่อนน้อม
          // คุณลักษณะที่ต้องการ (columns 24-33) - 10 หัวข้อลักษณะ
          desired_knowledge: cleanCellData(columns[24]), // มีความรู้ทางด้านวิชาการ
          desired_honesty: cleanCellData(columns[25]), // มีความซื่อสัตย์ สุจริต
          desired_creativity: cleanCellData(columns[26]), // มีความคิดสร้างสรรค์
          desired_critical_thinking: cleanCellData(columns[27]), // สามารถคิดเชิงวิพากษ์
          desired_teamwork: cleanCellData(columns[28]), // สามารถทำงานเป็นทีม
          desired_leadership: cleanCellData(columns[29]), // ความเป็นผู้นำ
          desired_problem_solving: cleanCellData(columns[30]), // การแก้ปัญหา
          desired_entrepreneurship: cleanCellData(columns[31]), // ความเป็นผู้ประกอบการ
          desired_language: cleanCellData(columns[32]), // ภาษาต่างประเทศ
          desired_communication: cleanCellData(columns[33]), // การสื่อสาร
          // ข้อมูลเพิ่มเติม (columns 34-39) - ใช้ safe access เนื่องจากอาจจะไม่ครบ
          strengths: cleanCellData(columns[34] || ''), // ลักษณะเด่นของนิสิต
          recommendations: cleanCellData(columns[35] || ''), // ข้อแนะนำสำหรับนิสิต
          required_skills: cleanCellData(columns[36] || ''), // ความรู้/เครื่องมือที่ควรมี
          suggested_courses: cleanCellData(columns[37] || ''), // คอร์สอบรมที่เป็นประโยชน์
          collaboration_ideas: cleanCellData(columns[38] || ''), // ความร่วมมือกับคณะ
          satisfaction: cleanCellData(columns[39] || ''), // ความพึงพอใจโดยรวม
          hasSpecialChars: hasSpecialChars, // เพิ่มข้อมูลว่ามี special characters หรือไม่
          lineNumber: index + 1,
          totalColumns: columns.length // เพิ่มข้อมูลจำนวนคอลัมน์
        });
      } else {
        // เพิ่มการ log ข้อมูลที่ถูกข้าม
        console.log(`Skipped line ${index + 1}: ${columns.length} columns, content: ${line.substring(0, 100)}...`);
      }
    });
    
    console.log(`Parsed ${parsedData.length} valid rows from ${lines.length} total lines`);
    return parsedData;
  };

  // Process bulk input
  const handleBulkSubmit = async () => {
    setIsProcessing(true); // เริ่มการประมวลผล
    
    try {
      const parsedData = parseBulkData(bulkInputData);
      
      if (parsedData.length === 0) {
        dispatch(updateSnackbar({
          open: true,
          severity: "error",
          content: "ไม่พบข้อมูลที่ถูกต้อง กรุณาตรวจสอบรูปแบบข้อมูล"
        }));
        setIsProcessing(false);
        return;
      }

      // แสดงข้อความเริ่มต้นการประมวลผล
      dispatch(updateSnackbar({
        open: true,
        severity: "info",
        content: `กำลังประมวลผลข้อมูล ${parsedData.length} รายการ...`
      }));

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      for (const item of parsedData) {
        try {
          // ใช้ req_id ที่ค้นหาได้จาก preview
          let student = null;
          let final_req_id = null;
          
          // ใช้ข้อมูลที่ค้นหาได้แล้วจาก preview
          if (item.found_req_id) {
            student = item.found_student;
            final_req_id = item.found_req_id;
          } else {
            // ค้นหาจากชื่อใหม่อีกครั้งด้วย utility function ที่ปรับปรุงแล้ว
            student = findStudentByName(item.student_name, tableData);
            final_req_id = student?.req_id;
          }

          if (!student || !final_req_id) {
            const searchInfo = item.found_req_id ? `req_id "${item.found_req_id}" จากการค้นหา` :
                              `ชื่อ "${item.student_name}"`;
            errors.push(`บรรทัดที่ ${item.lineNumber}: ไม่พบนักศึกษา ${searchInfo}`);
            errorCount++;
            continue;
          }

          const formData = {
            req_id: final_req_id,
            timestamp_text: item.timestamp_text,
            email: item.email,
            evaluator_name: item.evaluator_name,
            evaluator_position: item.evaluator_position,
            evaluator_phone: item.evaluator_phone,
            student_name: item.student_name,
            program: item.program,
            company: item.company,
            period: item.period,
            // คะแนนการประเมิน
            eval_work_effectiveness: item.eval_work_effectiveness,
            eval_initiative: item.eval_initiative,
            eval_communication: item.eval_communication,
            eval_learning: item.eval_learning,
            eval_compliance: item.eval_compliance,
            eval_responsibility: item.eval_responsibility,
            eval_leadership: item.eval_leadership,
            eval_relationship: item.eval_relationship,
            eval_teamwork: item.eval_teamwork,
            eval_confidence: item.eval_confidence,
            eval_consideration: item.eval_consideration,
            eval_commitment: item.eval_commitment,
            eval_enthusiasm: item.eval_enthusiasm,
            eval_improvement: item.eval_improvement,
            eval_respect: item.eval_respect,
            // คุณลักษณะที่ต้องการ
            desired_knowledge: item.desired_knowledge,
            desired_honesty: item.desired_honesty,
            desired_creativity: item.desired_creativity,
            desired_critical_thinking: item.desired_critical_thinking,
            desired_teamwork: item.desired_teamwork,
            desired_leadership: item.desired_leadership,
            desired_problem_solving: item.desired_problem_solving,
            desired_entrepreneurship: item.desired_entrepreneurship,
            desired_language: item.desired_language,
            desired_communication: item.desired_communication,
            // ข้อมูลเพิ่มเติม
            strengths: item.strengths,
            recommendations: item.recommendations,
            required_skills: item.required_skills,
            suggested_courses: item.suggested_courses,
            collaboration_ideas: item.collaboration_ideas,
            satisfaction: item.satisfaction
          };

          await addInternshipEvaluations(formData);

          successCount++;
        } catch (error) {
          errors.push(`บรรทัดที่ ${item.lineNumber}: เกิดข้อผิดพลาดในการบันทึกข้อมูลของ ${item.student_name}`);
          errorCount++;
        }
      }

      // Show results
      setIsProcessing(false); // หยุดสถานะการประมวลผลก่อน
      
      if (successCount > 0) {
        dispatch(updateSnackbar({
          open: true,
          severity: "success",
          content: `✅ บันทึกข้อมูลสำเร็จ ${successCount} รายการ${errorCount > 0 ? `, ❌ ไม่สำเร็จ ${errorCount} รายการ` : ''}`
        }));
        
        // แสดงข้อความสำเร็จ และรอให้ผู้ใช้เห็น
        setTimeout(async () => {
          try {
            // Refresh data ก่อน และรอให้เสร็จ
            if (onRefresh) {
              await onRefresh(); // ใช้ callback แทน reload
            } else {
              window.location.reload(); // fallback สำหรับกรณีที่ไม่มี callback
              return; // ไม่ต้องปิด modal เพราะจะ reload
            }
            
            // ปิด modal หลังจาก refresh เสร็จแล้ว
            handleClose();
          } catch (error) {
            // ถ้า refresh ไม่สำเร็จ ก็ยังปิด modal
            handleClose();
          }
        }, 1000); // ลดเวลาให้เร็วขึ้น
      } else if (errors.length > 0) {
        dispatch(updateSnackbar({
          open: true,
          severity: "error",
          content: `❌ บันทึกข้อมูลไม่สำเร็จทั้งหมด: ${errors.slice(0, 3).join(', ')}${errors.length > 3 ? '...' : ''}`
        }));
        
        // ไม่ปิด modal เมื่อมี error เพื่อให้ผู้ใช้แก้ไขได้
        // setIsProcessing(false) ได้ทำไว้ด้านบนแล้ว
      }

      // ลบ handleClose() ออกจากตรงนี้
    } catch (error) {
      dispatch(updateSnackbar({
        open: true,
        severity: "error",
        content: `❌ เกิดข้อผิดพลาดในการประมวลผลข้อมูล: ${error.message || 'ข้อผิดพลาดไม่ทราบสาเหตุ'}`
      }));
      
      // ไม่ปิด modal เมื่อเกิด error เพื่อให้ผู้ใช้ลองใหม่ได้
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setBulkInputData('');
    setPreviewData([]);
    setIsProcessing(false); // รีเซ็ต processing state
    onClose();
  };

  const getModalTitle = () => {
    return inputType === 'company' 
      ? 'เพิ่มคะแนนพี่เลี้ยงแบบกลุ่ม' 
      : 'เพิ่มคะแนนรายงานแบบกลุ่ม';
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="bulk-input-modal-title"
      aria-describedby="bulk-input-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '80%', md: '900px', lg: '1000px' }, // เพิ่มความกว้าง
          bgcolor: 'background.paper',
          borderRadius: '12px',
          boxShadow: 24,
          p: 4,
          maxHeight: '90vh',
          overflow: 'auto'
        }}
      >
        <Typography 
          id="bulk-input-modal-title" 
          variant="h6" 
          component="h2"
          sx={{ 
            fontFamily: 'TH Sarabun New, Arial, sans-serif',
            fontWeight: 'bold',
            marginBottom: 2,
            color: '#333'
          }}
        >
          📊 {getModalTitle()}
        </Typography>

        <Alert severity="info" sx={{ marginBottom: 2 }}>
          <strong>📋 วิธีใช้งาน:</strong>
          <br/>1. คัดลอกข้อมูลการประเมินทั้งแถวจาก Google Sheets (ครบ 40 คอลัมน์) เริ่มจากคอลัมน์ "ประทับเวลา"
          <br/>2. วางข้อมูลลงในช่องด้านล่าง
          <br/>3. ระบบจะค้นหา req_id ของนิสิตอัตโนมัติจากชื่อในข้อมูล
          <br/>💡 <strong>รองรับข้อมูลที่มีการขึ้นบรรทัดใหม่และเครื่องหมายคำพูด (Quotes) ใน Google Sheets</strong>
          {tableData && tableData.length > 0 && (
            <Box sx={{ marginTop: 1, fontSize: '0.85em', color: '#555', fontStyle: 'italic' }}>
              📊 มีข้อมูลนิสิตในระบบ {tableData.length} คน พร้อมสำหรับการค้นหาและจับคู่อัตโนมัติ
            </Box>
          )}
        </Alert>

        <FormControl fullWidth>
          <TextField
            multiline
            rows={8}
            value={bulkInputData}
            onChange={(e) => {
              setBulkInputData(e.target.value);
              // Update preview when input changes
              if (e.target.value.trim()) {
                const parsed = parseBulkData(e.target.value);
                setPreviewData(parsed); // Show all rows for preview
              } else {
                setPreviewData([]);
              }
            }}
            placeholder="วางข้อมูลการประเมินจาก Google Sheets ที่นี่..."
            label={`วางข้อมูลการประเมิน ${inputType === 'company' ? 'บริษัท' : 'รายงาน'}`}
            variant="outlined"
            sx={{
              marginBottom: 2,
              '& .MuiInputBase-input': {
                fontFamily: 'monospace',
                fontSize: '14px'
              }
            }}
          />
          
          <FormHelperText sx={{ marginBottom: 2 }}>
            <strong>วิธีใช้:</strong> 
            1. เลือกข้อมูลทั้งแถวจาก Google Sheets (ตั้งแต่คอลัมน์ A ถึง AN หรือมากกว่า) 
            2. กด Ctrl+C เพื่อคัดลอก 
            3. กด Ctrl+V ในช่องด้านบน 
            4. ตรวจสอบข้อมูลในตัวอย่าง (ต้องแสดง ✅ ในแถวที่ต้องการนำเข้า) 
            5. กดนำเข้าข้อมูล
            <br/>
            💡 <strong>ระบบจะจัดการข้อมูลที่มีการขึ้นบรรทัดใหม่และเครื่องหมายคำพูดอัตโนมัติ</strong><br/>
            🔍 <strong>ระบบจะค้นหาและจับคู่นิสิตโดยอัตโนมัติจากชื่อในฟอร์ม</strong>
          </FormHelperText>

          {/* Preview Section */}
          {previewData.length > 0 && (
            <Box sx={{ marginBottom: 2 }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  fontWeight: 'bold', 
                  marginBottom: 1,
                  color: '#4a90e2'
                }}
              >
                🔍 ตัวอย่างข้อมูลที่อ่านได้ ({previewData.length} แถว):
              </Typography>
              
              <Box
                sx={{
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '12px',
                  maxHeight: '400px', // เพิ่มความสูงให้แสดงได้มากขึ้น
                  overflow: 'auto',
                  fontSize: '12px'
                }}
              >
                {previewData.map((item, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      marginBottom: 1, 
                      padding: '8px',
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f5f5f5',
                      borderRadius: '4px',
                      border: item.found_req_id ? '2px solid #4caf50' : '2px solid #f44336',
                      boxShadow: item.found_req_id ? '0 2px 4px rgba(76, 175, 80, 0.1)' : '0 2px 4px rgba(244, 67, 54, 0.1)'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                      <Box sx={{ fontWeight: 'bold', color: item.found_req_id ? '#2e7d32' : '#d32f2f' }}>
                        แถวที่ {item.lineNumber}: {item.student_name || 'ไม่มีชื่อ'}
                      </Box>
                      
                      <Box sx={{ marginLeft: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                        {item.hasSpecialChars && (
                          <Chip
                            size="small"
                            label="📝 มีข้อความซับซ้อน"
                            sx={{
                              backgroundColor: '#fff3e0',
                              color: '#e65100',
                              fontSize: '9px',
                              height: '20px',
                              '& .MuiChip-label': {
                                padding: '0 6px'
                              }
                            }}
                          />
                        )}
                        
                        {item.found_req_id ? (
                          <Box sx={{ 
                            backgroundColor: '#e8f5e8', 
                            color: '#2e7d32', 
                            padding: '2px 8px', 
                            borderRadius: '12px',
                            fontSize: '10px',
                            fontWeight: 'bold'
                          }}>
                            ✅ ID: {item.found_req_id}
                          </Box>
                        ) : (
                          <Box sx={{ 
                            backgroundColor: '#ffeaea', 
                            color: '#d32f2f', 
                            padding: '2px 8px', 
                            borderRadius: '12px',
                            fontSize: '10px',
                            fontWeight: 'bold'
                          }}>
                            ❌ ไม่พบข้อมูล
                          </Box>
                        )}
                      </Box>
                    </Box>
                    
                    <Box sx={{ fontSize: '10px', color: '#999', marginBottom: '4px' }}>
                      📅 เวลา: {item.timestamp_text || 'ไม่มีข้อมูล'} | 📊 คอลัมน์: {item.totalColumns}
                      {item.hasSpecialChars && (
                        <span style={{ color: '#e65100', marginLeft: '8px' }}>
                          | 🔧 ประมวลผลข้อความพิเศษแล้ว
                        </span>
                      )}
                    </Box>
                  
                    {item.found_student && (
                      <Box sx={{ fontSize: '11px', color: '#4caf50', marginBottom: '4px' }}>
                        🎓 {item.found_student.st_name_th} {item.found_student.st_lastname_th} | 
                        🆔 รหัสนิสิต: {item.found_student.st_id || 'ไม่ระบุ'} | 
                        📚 สาขา: {item.program || item.found_student.major_name || 'ไม่ระบุ'}
                      </Box>
                    )}
                    
                    {!item.found_student && item.student_name && (
                      <Box sx={{ fontSize: '11px', color: '#f44336', marginBottom: '4px' }}>
                        ❌ ไม่พบข้อมูลนิสิตในระบบ: "{item.student_name}" | 📚 สาขา: {item.program || 'ไม่ระบุ'}
                      </Box>
                    )}
                    
                    <Box sx={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                      👤 ผู้ประเมิน: {item.evaluator_name || 'ไม่ระบุ'} ({item.evaluator_position || 'ไม่ระบุ'}) | 📧 {item.email || 'ไม่ระบุ'}
                    </Box>
                    
                    <Box sx={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                      🏢 สถานประกอบการ: {item.company || 'ไม่ระบุ'} | 🕒 ช่วงเวลา: {item.period || 'ไม่ระบุ'}
                    </Box>
                    
                    <Box sx={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>
                      📊 คะแนนตัวอย่าง: ประสิทธิผล({item.eval_work_effectiveness || '-'}) | 
                      การริเริ่ม({item.eval_initiative || '-'}) | 
                      การสื่อสาร({item.eval_communication || '-'}) | 
                      ภาวะผู้นำ({item.eval_leadership || '-'})
                    </Box>
                    
                    {item.strengths && (
                      <Box sx={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>
                        💪 จุดเด่น: {item.strengths.length > 60 ? item.strengths.substring(0, 60) + '...' : item.strengths}
                      </Box>
                    )}
                    
                    {item.recommendations && (
                      <Box sx={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>
                        💡 ข้อเสนอแนะ: {item.recommendations.length > 60 ? item.recommendations.substring(0, 60) + '...' : item.recommendations}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
              
              <Box sx={{ 
                marginTop: 2, 
                padding: '8px 12px',
                backgroundColor: '#f0f7ff',
                borderRadius: '6px',
                border: '1px solid #bbdefb',
                fontSize: '12px', 
                color: '#1565c0',
                textAlign: 'center',
                fontWeight: 'bold'
              }}>
                📊 <strong>สรุปข้อมูล:</strong> ทั้งหมด {previewData.length} รายการ | 
                <span style={{ color: '#2e7d32', marginLeft: '8px' }}>
                  ✅ พร้อมนำเข้า {previewData.filter(item => item.found_req_id).length} รายการ
                </span>
                {previewData.filter(item => !item.found_req_id).length > 0 && (
                  <span style={{ color: '#d32f2f', marginLeft: '8px' }}>
                    ❌ ไม่พบข้อมูล {previewData.filter(item => !item.found_req_id).length} รายการ
                  </span>
                )}
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', marginBottom: 2 }}>
            <Button
              onClick={handleClose}
              sx={{ 
                color: '#666',
                borderColor: '#666'
              }}
              variant="outlined"
            >
              ยกเลิก
            </Button>
            
            <Button
              variant="contained"
              onClick={handleBulkSubmit}
              disabled={!bulkInputData.trim() || isProcessing}
              sx={{ 
                backgroundColor: isProcessing ? '#9e9e9e' : '#4a90e2',
                color: '#fff',
                '&:hover': {
                  backgroundColor: isProcessing ? '#9e9e9e' : '#357ab8',
                },
                '&:disabled': {
                  backgroundColor: '#ccc'
                }
              }}
            >
              {isProcessing ? '⏳ กำลังประมวลผล...' : 'นำเข้าข้อมูล'}
            </Button>
          </Box>
        </FormControl>
      </Box>
    </Modal>
  );
};

export default BulkInputModal;